import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Music, Check, Library, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, mediaUrl, brand } from "@/lib/api";
import { fmtDims } from "@/lib/useModels";

export const KINDS = [["", "All"], ["image", "Images"], ["video", "Videos"], ["audio", "Audio"]];
export const SOURCES = [["", "Everything"], ["upload", "Uploaded"], ["generated", "Generated"]];

const accepts = (accept, mime) => !accept || accept.split(",").some((a) => { a = a.trim(); return a.endsWith("/*") ? mime.startsWith(a.slice(0, -1)) : a === mime || (a === "video/quicktime" && mime.startsWith("video/")); });

export const useLibrary = (open, tick = 0) => {
  const [items, setItems] = useState(null);
  useEffect(() => { if (open) api.get("/library").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [open, tick]);
  return items;
};

export const LibTile = ({ it, selected, onClick, testid, onDelete }) => {
  const m = it.mime || "";
  return (
    <div className="relative group/tile">
    <button type="button" onClick={onClick} data-testid={testid}
      className={`relative w-full rounded-lg overflow-hidden border text-left bg-[#0D1016] transition-colors ${selected ? "border-[#7AF25F]" : "border-zinc-800 hover:border-zinc-600"}`}>
      <div className="aspect-video bg-black flex items-center justify-center">
        {m.startsWith("video/") ? <video src={`${mediaUrl(it.url)}#t=${it.duration && it.duration < 1 ? 0 : 0.5}`} muted preload="metadata" className="w-full h-full object-cover" />
          : m.startsWith("audio/") ? <Music className="w-6 h-6 text-zinc-500" />
          : <img src={mediaUrl(it.url)} alt="" loading="lazy" className="w-full h-full object-cover" />}
      </div>
      {selected && <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-[#7AF25F] text-black flex items-center justify-center"><Check className="w-3 h-3" /></span>}
      <span className={`absolute top-1 left-1 ax-badge !bg-black/75 !text-[9px] ${it.source === "generated" ? "!text-[#7AF25F]" : "!text-zinc-200"}`}>{it.source === "generated" ? "Generated" : "Upload"}</span>
      <div className="p-1.5">
        <div className="text-[10px] text-zinc-200 truncate">{it.name}</div>
        <div className="text-[9px] font-mono text-zinc-500 truncate">{[it.project_name, fmtDims(it.width, it.height), it.duration ? `${Math.round(it.duration)}s` : null, it.source === "generated" ? brand(it.model_name) : null].filter(Boolean).join(" · ")}</div>
      </div>
    </button>
    {onDelete && <button type="button" onClick={() => onDelete(it)} data-testid={`${testid}-delete`} title="Delete permanently"
      className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/80 border border-white/15 text-zinc-300 hover:bg-red-500/80 hover:text-white flex items-center justify-center opacity-0 group-hover/tile:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
  );
};

export const filterLib = (items, { kind, source, q, accept, project }) => (items || []).filter((x) => (!kind || (x.mime || "").startsWith(kind)) && (!source || x.source === source)
  && (!project || x.project_id === project)
  && accepts(accept, x.mime || "") && (!q || `${x.name} ${x.prompt || ""} ${x.project_name}`.toLowerCase().includes(q.toLowerCase())));

export const ProjectFilter = ({ value, onChange, testid }) => {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {}); }, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="ax-input !h-10 !py-0 text-xs w-44" data-testid={testid} title="Filter by project">
      <option value="">All Projects</option>
      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
};

// Turn library entries into project references (generated results are copied once, uploads are reused as-is).
export const toRefs = (list) => Promise.all(list.map((it) => it.source === "generated"
  ? api.post("/references/from-generation", { generation_id: it.generation_id, index: it.index, name: `${it.name}.${(it.mime || "").split("/")[1] || "png"}` }).then((r) => r.data)
  : Promise.resolve(it)));

export default function LibraryPicker({ open, onOpenChange, accept, max = 1, onPick }) {
  const items = useLibrary(open);
  const [kind, setKind] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [project, setProject] = useState(() => (sessionStorage.getItem("ax_last_studio") || "").split("/")[2] || "");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSel([]); setProject((sessionStorage.getItem("ax_last_studio") || "").split("/")[2] || ""); } }, [open]);
  const shown = useMemo(() => filterLib(items, { kind, source, q, accept, project }), [items, kind, source, q, accept, project]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : max === 1 ? [id] : s.length >= max ? s : [...s, id]));
  const use = async () => {
    setBusy(true);
    try {
      const refs = await toRefs(sel.map((id) => items.find((x) => x.id === id)));
      onPick(refs);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, "Could not use that file")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0D12] border-zinc-800 text-zinc-100 max-w-4xl max-h-[90vh] flex flex-col" data-testid="library-picker" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Library className="w-5 h-5 text-[#7AF25F]" />Media Library</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5">
          {KINDS.filter(([k]) => !k || !accept || accept.includes(`${k}/`)).map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} data-testid={`library-kind-${k || "all"}`} className={`ax-chip ${kind === k ? "!border-[#7AF25F]/60 text-white" : ""}`}>{l}</button>)}
          <span className="w-px h-5 bg-zinc-800 mx-1" />
          {SOURCES.map(([k, l]) => <button key={k} type="button" onClick={() => setSource(k)} data-testid={`library-source-${k || "all"}`} className={`ax-chip ${source === k ? "!border-[#7AF25F]/60 text-white" : ""}`}>{l}</button>)}
          <ProjectFilter value={project} onChange={setProject} testid="library-project-filter" />
          <div className="relative ml-auto"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files" className="ax-input !h-9 !pl-8 w-48 text-xs" data-testid="library-search" /></div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-[200px]">
          {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
            : !shown.length ? <p className="py-16 text-center text-sm text-zinc-500" data-testid="library-empty">No matching files yet.</p>
            : <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="library-grid">{shown.map((it) => <LibTile key={it.id} it={it} selected={sel.includes(it.id)} onClick={() => toggle(it.id)} testid={`library-item-${it.id}`} />)}</div>}
        </div>
        <button type="button" onClick={use} disabled={!sel.length || busy} data-testid="library-use"
          className="h-11 rounded-lg ax-generate text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{sel.length ? `Use ${sel.length} file${sel.length > 1 ? "s" : ""}` : `Pick up to ${max}`}</button>
      </DialogContent>
    </Dialog>
  );
}
